import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const TechServiceHistory = () => {
  const [serviceCall, setServiceCall] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  // Fetch full list on mount and when apiUrl changes
  useEffect(() => {
    fetchServiceCall();
    // eslint-disable-next-line
  }, [apiUrl]);

  const fetchServiceCall = async () => {
    try {
      setIsSearching(true);
      const response = await axios.get(`${apiUrl}/service_crud.php?serviceHistory=true`);
      if (response.data[0] === "success") {
        setServiceCall(response.data[1] || []);
      } else {
        setServiceCall([]);
        toast.error("No service call data found");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate("/login");
      } else {
        toast.error("Error loading service calls");
      }
    }
    setIsSearching(false);
  };

  // Search function calls the API with the searchText
  const searchServiceCall = async () => {
    if (!searchText.trim()) {
      fetchServiceCall();
      return;
    }
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${apiUrl}/service_crud.php?serviceHistory=true&search=${encodeURIComponent(
          searchText
        )}`
      );
      if (response.data[0] === "success") {
        setServiceCall(response.data[1] || []);
      } else {
        setServiceCall([]);
        toast.error("No service call data found");
      }
    } catch (error) {
      toast.error("Error searching service calls");
    }
    setIsSearching(false);
  };

  const openModal = (inspectionId) => {
    const item = serviceCall.find((insp) => insp.id === inspectionId);
    setSelectedInspection(item);
  };

  const closeModal = () => {
    setSelectedInspection(null);
  };

  return (
    <div className="p-4 max-w-md mx-auto">
      {/* Top bar with search tab */}
      <div className="flex items-center justify-between mb-2">
      <h1 className="text-xl font-bold">
        {showSearch && searchText.trim() ? 'Results' : 'Last 10 calls'}
      </h1>
      <button
        className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
        onClick={() => setShowSearch((s) => !s)}
      >
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-700" />
      </button>
    </div>


      {/* Search bar (appears on tab click) */}
      {showSearch && (
        <form
          className="mb-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            searchServiceCall();
          }}
        >
          <input
            type="text"
            placeholder="Search ..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring focus:border-blue-300"
            autoFocus
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isSearching}
          >
            {isSearching ? "..." : "Search"}
          </button>
        </form>
      )}

      {/* Service Call List */}
      <ul className="space-y-2">
        {serviceCall.length === 0 && !isSearching && (
          <li className="text-center text-gray-400 py-8">No service calls found.</li>
        )}
        {serviceCall.map((item) => (
          <li
            key={item.id}
            className="bg-white rounded shadow p-3 cursor-pointer"
            onClick={() => openModal(item.id)}
          >
            <div className="flex">
              <div className="mr-3 text-center">
                <div className="text-2xl font-bold">{item.id}</div>
                <div className="text-sm text-gray-500">{item.DATE}</div>
                <div className="text-xs text-blue-500">{item.STATUS}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400">
                  {item.CUSTOMER}
                  <div className="font-bold text-gray-800">{item.ADDRESS}</div>
                </div>
                <div className="text-gray-400 text-xs py-1">
                  by: {item.TECH}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Modal for selected inspection details */}
      {selectedInspection && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-11/12 max-w-md">
            <h3 className="text-lg font-bold mb-4">Details</h3>
            <p className="mb-1">
              <span className="font-semibold">Status:</span> {selectedInspection.STATUS}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Trouble:</span> {selectedInspection.TROUBLE}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Address:</span> 
              <span className="text-blue-800">{selectedInspection.ADDRESS}</span>
            </p>
            <p className="mb-1">
              <span className="font-semibold">Tech:</span> {selectedInspection.TECH}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Date:</span>{" "}
              {selectedInspection.DATE} @ {selectedInspection.TIME} - {selectedInspection.end_time}
            </p>
            <br/>
            <hr/>
            <p className="mb-4">
              <span className="font-semibold">Notes:</span> {selectedInspection.NOTES}
            </p>
            <button
              onClick={closeModal}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1 px-4 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechServiceHistory;
